import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.array.sort.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.array.map.js";
import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { differenceInSeconds } from 'date-fns';
import { useFetchUserMeetingAnalysis } from 'actions/users';
import useSearchParameter from 'hooks/useSearchParameter';
import Heading from 'components/Heading';
import EmptyStateController from 'components/EmptyStateController';
import Stack from 'components/Stack';
import InlineList from 'components/InlineList';
import FriendlyDuration from 'components/FriendlyDuration';
import Box from 'components/Box';
import SkillScoreFlagCard from '../components/SkillScoreFlagCard';
import SummaryFlagCard from '../components/SummaryFlagCard';
import SkillPageWrapper from '../components/SkillPageWrapper';
import NoTrackedMeetingsEmptyContent from '../components/NoTrackedMeetingsEmptyContent';
import Flex from 'components/Flex';
import HelixInfoButton from '../components/HelixInfoButton';
import Feedback from '../components/Feedback';
function MeetingAnalysisPage(_ref) {
  var _meeting$userMeetingS, _meeting$userMeetingS2, _meeting$userMeetingS3, _meeting$userMeetingS4;
  var isCurrentUser = _ref.isCurrentUser,
    userId = _ref.userId,
    user = _ref.user,
    getUserSkillLocation = _ref.getUserSkillLocation;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useParams = useParams(),
    meetingId = _useParams.meetingId;
  var _useSearchParameter = useSearchParameter('skillId'),
    _useSearchParameter2 = _slicedToArray(_useSearchParameter, 1),
    skillIdToScrollTo = _useSearchParameter2[0];
  var cardRefs = useRef({});
  var _useFetchUserMeetingA = useFetchUserMeetingAnalysis({
      userId: userId,
      meetingId: meetingId
    }),
    meeting = _useFetchUserMeetingA.data,
    isLoading = _useFetchUserMeetingA.isLoading,
    isError = _useFetchUserMeetingA.isError;
  var startTime = meeting !== null && meeting !== void 0 && meeting.startTime ? new Date(meeting.startTime) : undefined;
  var formattedDate = startTime ? startTime.toLocaleDateString('en-GB', {
    dateStyle: 'medium'
  }) : undefined;
  var formattedTime = startTime ? startTime.toLocaleTimeString('en-GB', {
    timeStyle: 'short',
    hour12: true
  }) : undefined;
  var duration = meeting !== null && meeting !== void 0 && meeting.endTime && meeting !== null && meeting !== void 0 && meeting.startTime ? differenceInSeconds(new Date(meeting.endTime), new Date(meeting.startTime)) : undefined;
  var skillsWithScore = meeting === null || meeting === void 0 ? void 0 : (_meeting$userMeetingS = meeting.userMeetingSkills) === null || _meeting$userMeetingS === void 0 ? void 0 : (_meeting$userMeetingS2 = _meeting$userMeetingS.filter(function (skill) {
    return (skill === null || skill === void 0 ? void 0 : skill.score) || (skill === null || skill === void 0 ? void 0 : skill.score) === 0;
  })) === null || _meeting$userMeetingS2 === void 0 ? void 0 : _meeting$userMeetingS2.sort(function (a, b) {
    return a.name > b.name ? 1 : -1;
  });
  var skillsWithoutScore = meeting === null || meeting === void 0 ? void 0 : (_meeting$userMeetingS3 = meeting.userMeetingSkills) === null || _meeting$userMeetingS3 === void 0 ? void 0 : (_meeting$userMeetingS4 = _meeting$userMeetingS3.filter(function (skill) {
    return !(skill !== null && skill !== void 0 && skill.score) && (skill === null || skill === void 0 ? void 0 : skill.score) !== 0;
  })) === null || _meeting$userMeetingS4 === void 0 ? void 0 : _meeting$userMeetingS4.sort(function (a, b) {
    return a.name > b.name ? 1 : -1;
  });

  // Scroll the selected skill into view if skillId is in search params
  useLayoutEffect(function () {
    if (!skillIdToScrollTo) return;
    var target = cardRefs.current[skillIdToScrollTo];
    if (target) {
      var offset = 100; // Prevent header from overlapping
      var targetTop = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      });
    }
  }, [skillIdToScrollTo, skillsWithScore]);
  return /*#__PURE__*/React.createElement(SkillPageWrapper, {
    fallbackLink: getUserSkillLocation({}),
    user: user
  }, /*#__PURE__*/React.createElement(EmptyStateController, {
    isLoading: isLoading,
    isError: isError,
    isEmpty: !meeting,
    emptyContent: /*#__PURE__*/React.createElement(NoTrackedMeetingsEmptyContent, {
      isCurrentUser: isCurrentUser
    })
  }, /*#__PURE__*/React.createElement(Flex, {
    gap: "xxs",
    breakpoints: "xs",
    flexDirection: ['column-reverse', 'row'],
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Box, {
    flexAlign: "left"
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h2"
  }, meeting === null || meeting === void 0 ? void 0 : meeting.title)), /*#__PURE__*/React.createElement(Box, {
    flexAlign: "right"
  }, /*#__PURE__*/React.createElement(HelixInfoButton, null))), /*#__PURE__*/React.createElement(Box, {
    fontSize: "s",
    mt: "xxs",
    mb: "m"
  }, /*#__PURE__*/React.createElement(InlineList, {
    splitBy: "dot",
    as: "p",
    itemAs: "span",
    display: "inline",
    dimmed: true
  }, formattedDate, formattedTime, duration && /*#__PURE__*/React.createElement(FriendlyDuration, {
    value: duration
  }), /*#__PURE__*/React.createElement(React.Fragment, null, t('skills_organised_by', {
    user: meeting === null || meeting === void 0 ? void 0 : meeting.organiserEmail
  })), meeting === null || meeting === void 0 ? void 0 : meeting.meetingPlatform)), (meeting === null || meeting === void 0 ? void 0 : meeting.summary) && /*#__PURE__*/React.createElement(Box, {
    mb: "l",
    maxWidth: 860
  }, /*#__PURE__*/React.createElement(SummaryFlagCard, {
    summary: meeting.summary
  })), /*#__PURE__*/React.createElement(Stack, {
    spacing: "l",
    maxWidth: 860,
    as: "list"
  }, skillsWithScore === null || skillsWithScore === void 0 ? void 0 : skillsWithScore.map(function (skill) {
    var skillId = String(skill.skillId);
    return /*#__PURE__*/React.createElement("div", {
      key: "meeting-skill-".concat(skillId),
      ref: function ref(el) {
        if (el) cardRefs.current[skillId] = el;
      }
    }, /*#__PURE__*/React.createElement(SkillScoreFlagCard, {
      isCurrentUser: isCurrentUser,
      skill: skill,
      getLink: getUserSkillLocation
    }), isCurrentUser && skill.id && /*#__PURE__*/React.createElement(Feedback, {
      meetingId: meetingId,
      userId: userId,
      userMeetingSkillId: skill.id,
      feedback: skill.feedback,
      skillName: skill.name
    }));
  }), skillsWithoutScore === null || skillsWithoutScore === void 0 ? void 0 : skillsWithoutScore.map(function (skill) {
    return /*#__PURE__*/React.createElement(SkillScoreFlagCard, {
      key: "meeting-skill-".concat(skill === null || skill === void 0 ? void 0 : skill.skillId),
      isCurrentUser: isCurrentUser,
      skill: skill,
      userId: userId,
      getLink: getUserSkillLocation
    });
  }))));
}
export default MeetingAnalysisPage;